import React from 'react';
import type { Machine } from '../../types';
import { Cpu, Clock, AlertTriangle, CheckCircle2, ChevronDown } from 'lucide-react';

interface MachineSelectorProps {
  machines: Machine[];
  selectedMachineId: string;
  onSelectMachine: (machineId: string) => void;
}

export const MachineSelector: React.FC<MachineSelectorProps> = ({
  machines,
  selectedMachineId,
  onSelectMachine,
}) => {
  const selectedMachine = machines.find((m) => m.id === selectedMachineId) || machines[0];

  const getStatusBadge = (status: Machine['status']) => {
    switch (status) {
      case 'OVERDUE':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertTriangle className="w-3.5 h-3.5" />
            Overdue
          </span>
        );
      case 'DUE_SOON':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <Clock className="w-3.5 h-3.5" />
            Due Soon
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Normal
          </span>
        );
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Left: Machine Selection Control */}
        <div className="flex-1">
          <label
            htmlFor="machine-select"
            className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5"
          >
            <Cpu className="w-4 h-4 text-blue-600" />
            เลือกเครื่องจักรเป้าหมาย (Target Machine ID)
          </label>
          <div className="relative">
            <select
              id="machine-select"
              value={selectedMachineId}
              onChange={(e) => onSelectMachine(e.target.value)}
              className="w-full bg-white border border-slate-300 hover:border-slate-400 rounded-lg px-3.5 py-3 pr-10 text-slate-900 font-bold text-sm sm:text-base focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[48px] appearance-none cursor-pointer transition-colors shadow-xs"
            >
              {machines.map((m) => (
                <option key={m.id} value={m.id} className="bg-white text-slate-900 py-2">
                  {m.id} — {m.name} ({m.model})
                </option>
              ))}
            </select>
            <ChevronDown className="w-5 h-5 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Right: Quick Machine Health & Hours Card */}
        {selectedMachine && (
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 shrink-0">
            <div>
              <div className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-blue-600" />
                ชั่วโมงการทำงานสะสม
              </div>
              <div className="font-mono text-base sm:text-lg font-black text-blue-700">
                {selectedMachine.cumulativeHours.toLocaleString()} ชม.
              </div>
            </div>
            <div className="h-8 w-[1px] bg-slate-200" />
            <div className="text-right">
              <div className="text-[11px] text-slate-500 font-medium mb-0.5">สถานะบำรุงรักษา</div>
              {getStatusBadge(selectedMachine.status)}
            </div>
          </div>
        )}
      </div>

      {selectedMachine && (
        <div className="mt-3 pt-2.5 border-t border-slate-100 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-2">
          <span>
            Model: <strong className="text-slate-800 font-semibold">{selectedMachine.model}</strong> | S/N:{' '}
            <strong className="text-slate-800 font-semibold">{selectedMachine.serialNo}</strong>
          </span>
          <span>
            แม่แบบการตรวจ:{' '}
            <span className="font-mono text-blue-700 font-semibold">
              {selectedMachine.assignedTemplateId}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};
